import { BrandKit, VoiceParams } from 'types'
import Styles from './BrandVoice.module.scss'
import { ChangeEvent, useEffect, useState } from 'react'
import { useUpdateBrandVoice } from 'hooks/queries/workspaces/brandVoices/useUpdateBrandVoice'
import { useCreateBrandVoice } from 'hooks/queries/workspaces/brandVoices/useCreateBrandVoice'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { Section } from '../Section/Section'
import { Input } from 'almanac-ui/TextInputField/Input'
import { InputVariant } from 'almanac-ui/TextInputField/Types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { TagInput } from './TagInput'
import { Edit3 } from 'blaze-ui/icons/20'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useMobilePills } from 'blaze-ui/MobilePills/MobilePills'

type EditableVoiceParam = keyof Omit<VoiceParams, 'name'>

const AttributeEditable = ({
  value,
  onChange,
  onBlur,
  title,
  description,
  name,
  hideDescriptions = false,
  variant,
}: {
  value: string
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>
  title: string
  name: string
  description: string
  hideDescriptions?: boolean
  variant?: InputVariant
}) => (
  <div className={Styles.attribute}>
    <div className={Styles.heading}>
      <Heading level={3}>{title}</Heading>
      {!hideDescriptions && (
        <Text color="var(--dark-60)" lineClamp={1}>
          {description}
        </Text>
      )}
    </div>
    <div className={Styles.content}>
      <Input
        size="lg"
        isTextarea
        id={name}
        aria-label={name}
        name={name}
        value={value}
        className={Styles.textbox}
        onChange={onChange}
        onBlur={onBlur}
        variant={variant}
      />
    </div>
  </div>
)

const PillsAttributeEditable = ({
  value,
  title,
  description,
  placeholder,
  name,
  onChange,
  hideDescriptions = false,
  action,
  hideAddButton = false,
  showRemoveButton = true,
  isMobilePills = false,
}: {
  value: string[]
  onChange: (value: Set<string>) => void
  title: string
  name: string
  description: string
  placeholder?: string
  hideDescriptions?: boolean
  action?: React.ReactNode
  hideAddButton?: boolean
  showRemoveButton?: boolean
  isMobilePills?: boolean
}) => {
  const { Pills, EditPillsButton } = useMobilePills({
    values: value,
    title: title,
    onUpdate: (values: string[]) => onChange(new Set(values)),
  })

  return (
    <div className={Styles.attribute}>
      <div className={Styles.heading}>
        {action || isMobilePills ? (
          <div className={Styles.titleRow}>
            <Heading level={3}>{title}</Heading>
            {isMobilePills ? <EditPillsButton /> : action}
          </div>
        ) : (
          <Heading level={3}>{title}</Heading>
        )}
        {!hideDescriptions && (
          <Text color="var(--dark-60)" lineClamp={1}>
            {description}
          </Text>
        )}
      </div>
      <div className={Styles.content}>
        {isMobilePills ? (
          <Pills />
        ) : (
          <TagInput
            value={value}
            name={name}
            placeholder={placeholder || ''}
            onChange={onChange}
            hideAddButton={hideAddButton}
            showRemoveButton={showRemoveButton}
          />
        )}
      </div>
    </div>
  )
}

type Props = {
  brandKit: BrandKit
  onVoiceUpdate?: (voice: VoiceParams) => void
  isOnboarding?: boolean
  hideDescriptions?: boolean
  showEditButtons?: boolean
  onEditTags?: (title: string, tags: string[]) => void
  hideAddButton?: boolean
  compactSpacing?: boolean
  hideRemoveButtons?: boolean
}

export const BrandVoiceEditable = ({
  brandKit,
  onVoiceUpdate,
  isOnboarding,
  hideDescriptions = false,
  showEditButtons = false,
  onEditTags,
  hideAddButton = false,
  compactSpacing = false,
  hideRemoveButtons = false,
}: Props) => {
  const { isMobile } = useResponsiveDesign()
  const { brand_voice: brandVoice } = brandKit

  const initialVoiceParams = brandVoice?.voice_params ?? {
    name: `${brandKit.name} Brand Voice`,
    purpose: [],
    audience: [],
    character: [],
    emotions: [],
    language: [],
    syntax: [],
    tone: [],
  }

  const [voice, setVoice] = useState<VoiceParams>(initialVoiceParams)
  const { mutateAsync: updateBrandVoice } = useUpdateBrandVoice({ id: brandVoice?.id ?? 0 })
  const { mutateAsync: createBrandVoice } = useCreateBrandVoice()
  const { trackBrandVoiceConfirmed, trackBrandVoiceModalViewed } = useOnboardingTracking()

  const stringParams = JSON.stringify(brandVoice?.voice_params)

  useEffect(() => {
    brandVoice?.voice_params && setVoice(brandVoice.voice_params)
  }, [stringParams]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOnboarding) {
      trackBrandVoiceModalViewed()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleParamChange =
    (k: EditableVoiceParam) =>
    (tags: string[]): VoiceParams => {
      const newVoice = { ...voice, [k]: tags }
      setVoice(newVoice)
      return newVoice
    }

  const handleUpdate = async (newVoice: VoiceParams) => {
    const dirty = !brandVoice || JSON.stringify(newVoice) !== JSON.stringify(initialVoiceParams)

    if (!dirty) {
      return
    }

    if (brandVoice?.id) {
      if (onVoiceUpdate) {
        onVoiceUpdate(newVoice)
      } else {
        updateBrandVoice({ voice_params: newVoice })
      }
    } else {
      createBrandVoice({ voiceParams: newVoice, brandKitId: brandKit.id })
    }

    // Track brand voice confirmation only in onboarding context
    if (isOnboarding) {
      trackBrandVoiceConfirmed()
    }
  }

  const renderEditButton = (title: string, tags: string[]) => {
    if (!showEditButtons || !onEditTags || tags.length === 0) return undefined

    return (
      <IconButton variant="ghost" size="sm" onPress={() => onEditTags(title, tags)}>
        <Edit3 size={20} />
      </IconButton>
    )
  }

  return (
    <div className={Styles.root}>
      <Section>
        <div className={cx(Styles.attributes, { [Styles.compactAttributes]: compactSpacing })}>
          <AttributeEditable
            value={voice['purpose'].join(', ')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleParamChange('purpose')([e.currentTarget.value])}
            onBlur={() => {
              handleUpdate(voice)
            }}
            title="Purpose"
            description="What your brand stands for and why people should care."
            name="Purpose"
            hideDescriptions={hideDescriptions}
            variant={isMobile ? 'grey' : 'default'}
          />
          <AttributeEditable
            value={voice['audience'].join(', ')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleParamChange('audience')([e.currentTarget.value])}
            onBlur={() => {
              handleUpdate(voice)
            }}
            title="Audience"
            description="Who you're talking to in your marketing."
            name="Audience"
            hideDescriptions={hideDescriptions}
            variant={isMobile ? 'grey' : 'default'}
          />
          <PillsAttributeEditable
            value={voice['tone']}
            onChange={(value: Set<string>) => {
              const newVoice = handleParamChange('tone')(Array.from(value))
              handleUpdate(newVoice)
            }}
            placeholder="Relaxed, Formal, Friendly"
            title="Tone"
            description="How your brand sounds in every conversation."
            name="Tone"
            hideDescriptions={hideDescriptions}
            action={renderEditButton('Tone', voice['tone'])}
            hideAddButton={hideAddButton}
            showRemoveButton={!hideRemoveButtons}
            isMobilePills={isMobile}
          />
          <PillsAttributeEditable
            value={voice['emotions']}
            onChange={(value: Set<string>) => {
              const newVoice = handleParamChange('emotions')(Array.from(value))
              handleUpdate(newVoice)
            }}
            placeholder="Positive, Optimistic, Supportive"
            title="Emotion"
            description="The feelings your marketing should create."
            name="Emotion"
            hideDescriptions={hideDescriptions}
            action={renderEditButton('Emotion', voice['emotions'])}
            hideAddButton={hideAddButton}
            showRemoveButton={!hideRemoveButtons}
            isMobilePills={isMobile}
          />
          <PillsAttributeEditable
            value={voice['character']}
            onChange={(value: Set<string>) => {
              const newVoice = handleParamChange('character')(Array.from(value))
              handleUpdate(newVoice)
            }}
            placeholder="Professional, Playful, Authoritative"
            title="Character"
            description="The role your brand plays in customers' lives."
            name="Character"
            hideDescriptions={hideDescriptions}
            action={renderEditButton('Character', voice['character'])}
            hideAddButton={hideAddButton}
            showRemoveButton={!hideRemoveButtons}
            isMobilePills={isMobile}
          />
          <PillsAttributeEditable
            value={voice['syntax']}
            onChange={(value: Set<string>) => {
              const newVoice = handleParamChange('syntax')(Array.from(value))
              handleUpdate(newVoice)
            }}
            placeholder="Short, Use Contractions, Concise"
            title="Syntax"
            description="Your writing style and personality quirks."
            name="Syntax"
            hideDescriptions={hideDescriptions}
            action={renderEditButton('Syntax', voice['syntax'])}
            hideAddButton={hideAddButton}
            showRemoveButton={!hideRemoveButtons}
            isMobilePills={isMobile}
          />
          <PillsAttributeEditable
            value={voice['language']}
            onChange={(value: Set<string>) => {
              const newVoice = handleParamChange('language')(Array.from(value))
              handleUpdate(newVoice)
            }}
            placeholder="Simple, Jargon-free, Technical"
            title="Language"
            description="How formal or casual you sound."
            name="Language"
            hideDescriptions={hideDescriptions}
            action={renderEditButton('Language', voice['language'])}
            hideAddButton={hideAddButton}
            showRemoveButton={!hideRemoveButtons}
            isMobilePills={isMobile}
          />
        </div>
      </Section>
    </div>
  )
}
